import { useNotify, useTranslate } from 'react-admin';
import {
  Box,
  Card,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { useCopyToClipboard } from 'src/hooks/use-copy-to-clipboard';
import { useResponsive } from 'src/hooks/use-responsive';
import type { VaultHolder } from 'src/pages/admin/services/vaultService';

import { INVESTORS_NS as NS } from './constants';
import { holderCells, lockupText, middle } from './helpers';

/** PRD §14.5's fields, in its order: address (manager tagged), units, value, fee baseline, cumulative P&L, lock-up. */
const COLUMNS = ['address', 'units', 'value', 'hwm', 'pnl', 'lockup'] as const;
const NUMERIC = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;
const ID = { fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere' } as const;
const PNL_COLOR = { '-1': 'error.main', '0': 'text.primary', '1': 'success.main' } as const;

/**
 * A header with an explanation an operator reads once, not per row: the fee baseline (PRD §14.5, worded from
 * D-16) and the cumulative P&L (display only, against the cost basis — never what the profit share is charged on).
 */
const HINTS: Partial<Record<(typeof COLUMNS)[number], string>> = { hwm: 'hwmHint', pnl: 'pnlHint' };

const HintHeader = ({ column, hint }: { column: string; hint: string }) => {
  const translate = useTranslate();
  return (
    <>
      {translate(`${NS}.table.${column}`)}{' '}
      <Tooltip title={translate(`${NS}.${hint}`)} arrow>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            verticalAlign: 'middle',
            color: 'text.secondary',
            cursor: 'help',
          }}
        >
          <Iconify icon="eva:info-outline" width={16} />
        </Box>
      </Tooltip>
    </>
  );
};

/** A long id shown cut in the middle, whole on hover, copied whole. */
const LongId = ({
  value,
  head,
  tail,
  caption,
}: {
  value: string;
  head?: number;
  tail?: number;
  caption?: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { copy } = useCopyToClipboard();
  return (
    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', minWidth: 0 }}>
      <Tooltip title={value}>
        <Typography
          variant={caption ? 'caption' : 'body2'}
          sx={{
            ...ID,
            fontSize: caption ? 12 : 13,
            color: caption ? 'text.secondary' : 'text.primary',
          }}
        >
          {middle(value, head, tail)}
        </Typography>
      </Tooltip>
      <IconButton
        size="small"
        aria-label={translate(`${NS}.copy`)}
        onClick={async () => {
          if (await copy(value)) notify(translate(`${NS}.copied`), { type: 'info' });
        }}
        sx={{ p: 0.25, color: 'text.disabled' }}
      >
        <Iconify icon="eva:copy-outline" width={14} />
      </IconButton>
    </Stack>
  );
};

export const HoldersTable = ({ holders }: { holders: VaultHolder[] }) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');

  if (holders.length === 0) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {translate(`${NS}.empty`)}
        </Typography>
      </Card>
    );
  }

  const identity = (h: VaultHolder) => (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {h.address ? (
          <LongId value={h.address} />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {translate(`${NS}.noAddress`)}
          </Typography>
        )}
        {h.isManager && <Chip size="small" color="primary" label={translate(`${NS}.manager`)} />}
      </Stack>
      <LongId value={h.holderId} head={8} tail={6} caption />
    </Box>
  );

  const pnl = (h: VaultHolder) => {
    const cells = holderCells(h);
    return (
      <>
        <Box component="span" sx={{ color: PNL_COLOR[cells.pnlSign] }}>
          {cells.pnl}
        </Box>
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
          {translate(`${NS}.avgCost`, { nav: cells.avgCost })}
        </Typography>
      </>
    );
  };

  const lockup = (h: VaultHolder) => {
    const { label, detail } = lockupText(h, translate);
    return (
      <>
        <Chip
          size="small"
          variant="outlined"
          color={h.lockupStatus === 'UNLOCKED' ? 'default' : 'warning'}
          label={label}
        />
        {detail && (
          <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {detail}
          </Typography>
        )}
      </>
    );
  };

  if (smDown) {
    return (
      <Stack spacing={2}>
        {holders.map((h) => {
          const cells = holderCells(h);
          return (
            <Card key={h.holderId} sx={{ p: 2 }}>
              {identity(h)}
              <Box
                component="dl"
                sx={{
                  m: 0,
                  mt: 1.5,
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                }}
              >
                {[
                  ['units', cells.units],
                  ['value', cells.value],
                  ['hwm', cells.hwm],
                  ['pnl', pnl(h)],
                  ['lockup', lockup(h)],
                ].map(([key, value]) => (
                  <Box
                    key={key as string}
                    // The lock-up carries a long label and a date: it takes the whole row on a phone.
                    sx={{ minWidth: 0, gridColumn: key === 'lockup' ? '1 / -1' : undefined }}
                  >
                    <Typography component="dt" variant="caption" sx={{ color: 'text.secondary' }}>
                      {translate(`${NS}.table.${key}`)}
                    </Typography>
                    <Typography
                      component="dd"
                      variant="body2"
                      sx={{ m: 0, overflowWrap: 'anywhere' }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          );
        })}
      </Stack>
    );
  }

  return (
    <Card>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 960 }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c} sx={{ whiteSpace: 'normal', verticalAlign: 'bottom' }}>
                  {HINTS[c] ? (
                    <HintHeader column={c} hint={HINTS[c]} />
                  ) : (
                    translate(`${NS}.table.${c}`)
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {holders.map((h) => {
              const cells = holderCells(h);
              return (
                <TableRow key={h.holderId} selected={h.isManager}>
                  <TableCell sx={{ minWidth: 250 }}>{identity(h)}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.units}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.value}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.hwm}</TableCell>
                  <TableCell sx={NUMERIC}>{pnl(h)}</TableCell>
                  <TableCell>{lockup(h)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};
